/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import apiService from "services/apiService";
import { getApiErrorMessage } from "util/errorMessage";
import Input from "components/Input";
import PhoneNumberInput from "components/PhoneNumberInput";
import {
  DEFAULT_COUNTRY_CODE,
  joinPhoneNumber,
  phoneNumberSchema,
  splitPhoneNumber,
} from "util/phoneCountry";

export enum UserRole {
  USER = 1,
  ADMIN = 2,
  SYS_ADMIN = 3,
  MANAGER = 4,
}

const schema = yup.object().shape({
  mobileNumber: phoneNumberSchema("mobileCountryCode", {
    requiredMessage: "Mobile Number is Required",
  }),
  mobileCountryCode: yup.string().optional().default(DEFAULT_COUNTRY_CODE),

  emergencyMobileNumber: phoneNumberSchema("emergencyMobileCountryCode", {
    requiredMessage: "Emergency Mobile Number is Required",
  }),
  emergencyMobileCountryCode: yup.string().optional().default(DEFAULT_COUNTRY_CODE),

  state: yup
    .string()
    .trim()
    .required("State Name is Required")
    .matches(/^[A-Za-z ]+$/, "State Name should only contain letters"),

  permanentAddress: yup
    .string()
    .trim()
    .required("Permanent Address is Required")
    .matches(/[a-zA-Z0-9 ]/, "Invalid characters in Permanent Address"),

  tempAddress: yup
    .string()
    .trim()
    .required("Address is Required")
    .matches(/[a-zA-Z0-9]/, "Invalid characters in Temp Address"),

  PAN: yup
    .string()
    .required("PAN Number is Required")
    .trim()
    // Transform to uppercase before validating
    .transform((value) => (value ? value.toUpperCase() : value))
    .matches(
      /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
      "PAN should be in format: ABCDE1234F"
    ),

  aadhaar: yup
    .string()
    .required("Aadhaar Number is Required")
    .trim()
    .matches(/^[0-9]{12}$/, "Aadhaar Number must be exactly 12 digits"),

  city: yup
    .string()
    .trim()
    .required("City Name is Required")
    .matches(/^[A-Za-z ]+$/, "City Name should only contain letters"),

  pinCode: yup
    .string()
    .required("Pincode is Required")
    .trim()
    .matches(/^[0-9]{6}$/, "Pincode must be exactly 6 digits"),

  displayName: yup.string().required("Username is Required"),

  DOB: yup
    .string()
    .required("Date of Birth is Required")
    .test("age", "You must be at least 18 years old", (value) => {
      if (!value) return false;
      const today = new Date();
      const birthDate = new Date(value);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDifference = today.getMonth() - birthDate.getMonth();
      if (
        monthDifference < 0 ||
        (monthDifference === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }
      return age >= 18;
    }),

  // --- UAN VALIDATION ---
  // UAN is optional. `excludeEmptyString` is required because this yup version
  // fails `matches` on an empty string; with it, a blank UAN passes and the
  // 12-digit format is only enforced once the user actually types a value.
  UAN: yup
    .string()
    .trim()
    .matches(/^[0-9]{12}$/, {
      message: "UAN must be exactly 12 digits",
      excludeEmptyString: true,
    })
    .optional(),
});

interface updateProps {
  onClose: () => void;
  data: any;
  setToastData: any;
  setRefresh: any;
  currentUserRole?: UserRole; // Passed in to check if the current user is an ADMIN
}

export default function UpdateProfile({
  onClose,
  data,
  setToastData,
  setRefresh,
  currentUserRole,
}: updateProps) {
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...data,
      mobileNumber: splitPhoneNumber(data.mobileNumber).nationalNumber,
      mobileCountryCode: splitPhoneNumber(data.mobileNumber).countryCode,
      emergencyMobileNumber: splitPhoneNumber(data.emergencyMobileNumber).nationalNumber,
      emergencyMobileCountryCode: splitPhoneNumber(data.emergencyMobileNumber).countryCode,
      DOB: data.DOB ? new Date(data.DOB).toISOString().split("T")[0] : "",
      aadhaar: data.profileDetails?.aadhaar || data.aadhaar || "",
      PAN: data.profileDetails?.PAN || data.PAN || "",
      // Add UAN to default values
      UAN: data.profileDetails?.UAN || data.UAN || "",
    },
  });

  const { isDirty } = methods.formState;

  // Determine if the UAN field should be disabled
  const hasExistingUAN = Boolean(data?.profileDetails?.UAN || data?.UAN);
  const activeRole = currentUserRole || data?.role; // Fallback to data.role if prop isn't passed
  const isUanDisabled = hasExistingUAN && activeRole !== UserRole.ADMIN;

  const onSubmit: SubmitHandler<any> = (formData: any) => {
    const { mobileCountryCode, emergencyMobileCountryCode, ...profileValues } = formData;

    const payload = {
      ...profileValues,
      mobileNumber: joinPhoneNumber(mobileCountryCode, formData.mobileNumber),
      emergencyMobileNumber: joinPhoneNumber(
        emergencyMobileCountryCode,
        formData.emergencyMobileNumber
      ),
      profileDetails: {
        ...(data.profileDetails || {}),
        aadhaar: formData.aadhaar,
        UAN: formData.UAN, // Explicitly included in profile details (or root via ...formData)
      },
    };

    apiService
      .put("api/user", payload)
      .then((res: any) => {
        setToastData({
          message: "Profile Data is updated successfully!",
          severity: "info",
        });
        setRefresh("1", res?.data || formData);
      })
      // CHANGE: the handler took no argument, so the server's response was
      // discarded and every failure surfaced as the same generic sentence —
      // a 400 such as "UAN already registered to another employee" or a
      // duplicate-PAN rejection read as "Error updating data", leaving the
      // user with nothing to act on. Routed through getApiErrorMessage, the
      // shared helper already used across AdminConfig and the employee list,
      // so this form reports failures the same way the rest of the app does.
      // NOTE: the previous sentence is kept as the fallback, so the wording is
      // unchanged whenever the server sends no usable message (network drop,
      // empty 500 body).
      .catch((error: unknown) => {
        setToastData({
          message: getApiErrorMessage(error, "Error updating data. Please try again."),
          severity: "error",
        });
      })
      .finally(() => {
        onClose();
      });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="grid gap-4 grid-cols-3">
          <Input
            name="displayName"
            label="Full Name"
            placeholder={`Enter Full Name`}
            disabled={true}
            compact
          />
          <Input name="DOB" required label="Date Of Birth" type="date" />
          <PhoneNumberInput
            name="mobileNumber"
            countryCodeName="mobileCountryCode"
            label="Mobile Number"
            placeholder={`Enter Mobile Number`}
            required
            compact
          />

          <PhoneNumberInput
            name="emergencyMobileNumber"
            countryCodeName="emergencyMobileCountryCode"
            label="Emergency Mobile Number"
            placeholder={`Enter Mobile Number`}
            required
            compact
          />
          <Input
            name="aadhaar"
            label="Aadhaar Number"
            placeholder={`Enter your aadhaar Number`}
            required
            compact
          />
          <Input
            name="PAN"
            label="Pan Card"
            placeholder={`Enter Pancard Number`}
            required
            compact
          />
          <Input
            name={`tempAddress`}
            label="Temp Address "
            placeholder={`Enter Address`}
            type="textarea"
            required
            compact
          />
          <Input
            name="permanentAddress"
            label="Permanent Address "
            placeholder={`Enter Permanent Address`}
            type="textarea"
            required
            compact
          />
          <Input
            name="city"
            label="City"
            placeholder={`Eg: Bengaluru`}
            required
            compact
          />
          <Input
            name="state"
            label="State"
            placeholder={`Eg: Karnataka`}
            required
            compact
          />
          <Input
            name="pinCode"
            label="Pincode"
            placeholder={`Eg : 606567`}
            type="number"
            required
            compact
          />

          {/* UAN INPUT — optional */}
          <Input
            name="UAN"
            label="UAN Number"
            placeholder={`Eg : 100123456789`}
            type="number"
            disabled={isUanDisabled}
            compact
          />
        </div>

        <div className="flex justify-center mt-4">
          <button
            type="submit"
            disabled={!isDirty}
            className="bg-blue-500 text-white px-8 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:border-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Submit
          </button>
        </div>
      </form>
    </FormProvider>
  );
}